import { Box, BuildingComplex, Ellipsis, Eye } from 'lucide-react';
import equal from 'deep-equal';
import trash_black from 'images/ui_icon/trash_black.svg';
import * as moment from 'moment';
import * as React from 'react';
import styled from 'styled-components';
import { Field } from '../../atoms/forms';
import Add from '../../components_v2/add/Add';
import ClientCompany from '../../components_v2/clientCompany/ClientCompany';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import DropdownColor from '../../components_v2/dropdown/DropdownColors';
import Input from '../../components_v2/input/Input';
import { InputStyle } from '../../components_v2/input/model/Model';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import PopupCreation, { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { PanelSelector } from '../../components_v2/Selector/PanelSelector';
import Switch from '../../components_v2/Switch/Switch';
import { Column, Table, TableSortType } from '../../components_v2/table/Table';
import { RawDatePicker } from '../../components_v2/toolbarFilter/time/DatePicker';
import { DeleteDot } from '../../styles/global/css/Dot';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { Loader } from '../../styles/global/css/GlobalLoader';
import { PrimaryColor, BorderColor, BorderColor2, GreySidely3, RedSidely } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useRefState } from '../../utils/customHooks';
import StyledFieldEditor, { RawFieldEditor } from '../../utils/fieldEditor';
import { AlertContext, AlertRes } from '../alert/AlertProvider';
import useAlert from '../alert/UseAlert';
import { HorizontalContainer, SwitchText, TextArea, TextAreaContainer, TextAreaLabel } from '../calendar/style/NewEventStyle';
import { LeftContainer, RightContainer } from '../client-companies/style/PopupStyle';
import { TableCellButton } from '../client-companies/style/Style';
import { CreationPopupContainer, Label, TitleSection, creationPopupInputStyle as globalCreationPopupInputStyle } from '../../components_v2/popup/style/CreationFormStyle';
import Collapse from '../../components_v2/Collapse/Collapse';
import { Open } from '../../styles/global/css/Open';
import { normalizeCapital } from '../globals/FieldFunction';
import { LoadingStateEnum } from '../import/model';
import { Unhautorized } from '../noData/NoData';
import { TableRow, TableRowStatusBackground } from '../orders/templateOrders/style/Style';
import LightDropdown from '../../components_v2/dropdown/lightDropdown/LightDropdown';
import { DateAccessor } from '../client-companies/data/CompanyColumns';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import ProductPopup from '../products/product';
import { FlexDiv } from '../products/style';
import { deletePromotion, getPromotionById, PostPromotionBody, PutPromotionBody, putPromotionProductFields } from './actions';
import { isComputedField, promotionFieldColor, promotionFieldDisplay, promotionFieldRange } from './fieldDisplay';
import { CalculatedValueCell, calculatedSortValue } from './calculatedValue';
import { APromotionCalculatedFields, CalculatedFieldTarget } from '../../atoms/calculatedFields';
import { useRecoilValue } from 'recoil';
import { ScaleRange } from '../map/colorScale';
import { Dot } from '../../styles/global/css/Dot';
import { usePromotionFieldDefinitions } from './usePromotionFieldDefinitions';
import { Period, Promotion, PromotionCompany, PromotionProduct } from './model';
import { CompanyPerimeterPopup, PerimeterItem, ProductPerimeterPopup } from './PerimeterSelectionPopup';


const Trash = styled.img`
	width: 25px;
	height: 25px;
	cursor: pointer;
`;

const creationPopupInputStyle: InputStyle = {
	...globalCreationPopupInputStyle,
	border: `1px solid ${BorderColor2}`
};

export const creationPopupInputStyleMargin: InputStyle = {
	...creationPopupInputStyle,
	margin: `0 ${globalCreationPopupInputStyle.marginRight ?? 0} 21px ${globalCreationPopupInputStyle.marginLeft ?? 0}`
};

const Title = styled.h5<{ notRequired?: boolean }>`
	font-size: 12px;
	margin: 0;
	font-weight: 400;

	${({ notRequired }) => notRequired ? '' : `
		&:before {
			content: '*';
			color: ${RedSidely};
			margin-right: 3px;
		}
	`}
`;

const TitlePeriod = styled(Title)`
	font-size: 13px;
`;

function genDefaultPeriod(): Period {
	return {} as Period;
}

function PeriodEditor(props: { period: Period, index: number, setPeriods: React.Dispatch<React.SetStateAction<Period[]>>, readOnly: boolean | undefined, periods: Period[], fields: Field[] }) {
	const { period, index, setPeriods } = props;

	function onDatesChange(props: { startDate: moment.Moment, endDate: moment.Moment }) {
		period.start = props.startDate?.toISOString() ?? period.start;
		period.end = props.endDate?.toISOString() ?? period.end;
		setPeriods(p => [...p]);
	}
	return <FlexDiv gap='6px' flow='column' align='stretch' >
		<FlexDiv gap='0.5em' justify='space-between'>
			<TitlePeriod>{translateToString('period') + ' ' + index}</TitlePeriod>
			{!props.readOnly && <DeleteDot size='20px' onClick={() => setPeriods(p => {
				p.splice(index - 1, 1);
				return [...p];
			})} />}
		</FlexDiv>
		<FlexDiv gap='12px' flow='column' align='stretch' padding='15px' border={'1px solid ' + BorderColor2} borderRadius='5px'>
			<FlexDiv justify='space-between' align='stretch'>
				<FlexDiv gap='12px' flow='column'>
					<Title notRequired>{translateToString('color')}</Title>
					<DropdownColor
						dotSize='30px'
						disabled={props.readOnly}
						name={`periodColor[${index}]`}
						selectedValue={period.color ?? PrimaryColor}
						autoOptionUp
						onChange={v => {
							period.color = v.value;
							setPeriods(p => [...p]);
						}}
					/>
				</FlexDiv>
				<FlexDiv gap='6px' width='250px' flow='column' align='stretch'>
					<Title>{translateToString('period_name')}</Title>
					<Input
						required
						disabled={props.readOnly}
						inputStyle={creationPopupInputStyle}
						name={`periodTitle[${index}]`}
						type="text"
						placeholder={translateToString('name_period') + ' ' + index}
						normalize={[normalizeCapital]}
						onChange={value => {
							period.name = value;
							setPeriods(p => [...p]);
						}}
						value={period.name}
					/>
				</FlexDiv>
			</FlexDiv>

			<FlexDiv gap='6px' flow='column' align='stretch'>
				<Title>{translateToString('general.date')}</Title>
				<RawDatePicker
					button='hours'
					zIndex='unset'
					height='50px'
					fillWidth
					onTimeChange={onDatesChange}
					onDatesChange={onDatesChange}
					start={period.start ? moment(period.start) : null}
					end={period.end ? moment(period.end) : null}
					numberOfMonths={1}
					future
					readOnly={props.readOnly}
				/>
			</FlexDiv>
		</FlexDiv>
	</FlexDiv>;
}

const SeperatorWithColor = styled.div`
	width:100%;
	height:1px;
	background-color:${BorderColor};
`;

const LinkFieldValue = styled.div<{ justify: 'center' | 'left' }>`
	width: 100%;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	text-align: ${p => p.justify};
`;

// these editors open popups instead of blurring, so they close on click outside
const fieldsWithoutClickOutside = ['Multiselect', 'Date', 'TextArea', 'Boolean'];

// mirrors styleAdditionnalValue of the assortments grid: textual fields read
// from the left, everything else (numbers, dates, booleans) centers
function linkFieldJustify(field: Field): 'center' | 'left' {
	switch (field.type) {
		case 'Text':
		case 'Select':
		case 'Url':
		case 'Email':
		case 'Phone':
			return 'left';
		default:
			return 'center';
	}
}

// One promotion field cell, using the same widgets as the client companies
// listing for its additional values: Switch for booleans, LightDropdown for
// selects, the date picker cell for dates — the text-like and multiselect
// types keep the assortments-style inline editor (LinkFieldCell)
export function PromotionFieldCell(props: { field: Field, value: unknown, readonly?: boolean, onSave: (value: unknown) => Promise<void>, shown?: ScaleRange }) {
	const color = promotionFieldColor(props.field, props.value, props.shown);
	const cell = <PromotionFieldCellInner {...props} />;
	if (!color) return cell;
	// the value's colour, as a dot before it like the report cells
	return <FlexDiv gap='6px' width='100%' align='center'>
		<Dot size='10px' color={color} style={{ flexShrink: 0 }} />
		<div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
	</FlexDiv>;
}

function PromotionFieldCellInner(props: { field: Field, value: unknown, readonly?: boolean, onSave: (value: unknown) => Promise<void> }) {
	const { field, value } = props;
	const [saving, setSaving] = React.useState(false);
	const save = (v: unknown) => {
		setSaving(true);
		props.onSave(v).finally(() => setSaving(false));
	};
	if (saving) return <Loader width='15px' />;
	// a computed value is the backend's to write
	if (isComputedField(field)) return <TableRow>{promotionFieldDisplay(field, value)}</TableRow>;
	switch (field.type) {
		case 'Boolean':
			return <FlexDiv width='100%' justify='center'>
				<Switch value={!!value} readonly={props.readonly} onChange={save} />
			</FlexDiv>;
		case 'Select': {
			const display = value === null || value === undefined || value === '' ? '-' : String(value);
			if (props.readonly) return <TableRow>{display}</TableRow>;
			const options = Array.isArray(field.data) ? (field.data as string[]).filter(v => v !== null) : [];
			return <LightDropdown
				dropdownStyle={{ containerHeight: 'unset', optionWidth: '200px' }}
				datalist={[{ label: '-', value: null }, ...options.map(o => ({ label: o, value: o }))]}
				JSXButton={({ onClick }) => <TableRow onClick={onClick} cursor='pointer'>{display}</TableRow>}
				JSXContent={({ value, onClick }) => <div key={`PromotionFieldSelect[${field.id}][${value.label}]`} style={{ margin: 5 }}><TableRowStatusBackground onClick={onClick}>{value.label}</TableRowStatusBackground></div>}
				onChange={v => save(v.value)}
			/>;
		}
		case 'Date':
			if (props.readonly) return <TableRow>{value ? moment(value as string).format('L LT') : '-'}</TableRow>;
			return <DateAccessor date={(value as string) ?? null} onDateChange={date => save(date.toISOString())} />;
		default:
			return <LinkFieldCell {...props} />;
	}
}

// One editable cell of the per-product fields grid, modeled on the
// assortments Cell (RawFieldEditor pattern): display the value, click to
// edit, loader while the PUT saves. The value rides on the row object, so the
// columns never depend on it and keep their identity across edits.
export function LinkFieldCell(props: { field: Field, value: unknown, readonly?: boolean, onSave: (value: unknown) => Promise<void> }) {
	const { field, value } = props;
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [isActive, setIsActive] = React.useState(false);
	const cellRef = React.useRef<HTMLDivElement>(null);
	// one justification per field type, shared by the display, the '-'
	// placeholder and the edit input so nothing shifts when clicking to edit
	const justify = linkFieldJustify(field);

	React.useEffect(() => {
		if (!isActive || !fieldsWithoutClickOutside.includes(field.type)) return;
		const handleClickOutside = (event: MouseEvent) => {
			if (cellRef.current && !cellRef.current.contains(event.target as Node)) {
				setIsActive(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isActive, field.type]);

	if (loadingState === LoadingStateEnum.LOADING) return <Loader width='15px' />;
	if (isActive && !props.readonly) {
		// the popup Table's cells are 10px-padded flex boxes capped at 40px, so
		// unlike the assortments grid the editor sizes itself to the content
		// box: a borderless full-width input carrying the display's font and
		// justification (nothing shifts when the cell switches to edit mode),
		// and the fit-content editors (Boolean, Multiselect, Date, TextArea)
		// flex-justified like the display value
		return <div style={{ width: '100%' }} ref={cellRef}>
			<TableRow overflow={field.type === 'Multiselect' ? 'visible' : 'hidden'} onBlur={() => {
				if (!fieldsWithoutClickOutside.includes(field.type)) setIsActive(false);
			}}>
				<FlexDiv width='100%' justify={justify === 'center' ? 'center' : 'flex-start'}>
					<RawFieldEditor
						field={field}
						value={value}
						autoFocus
						searchableMultiselect={false}
						inputStyle={field.type === 'Multiselect'
							// full cell width like the popup multiselect fields
							// (creationPopupInputStyle): the selected chips get the whole
							// cell and scroll instead of clipping against a fixed box
							? { width: '100%', containerWidth: '100%', optionWidth: '200px', optionHeight: '200px' }
							: { border: 'none', backgroundColor: 'transparent', padding: '0', width: '100%', containerWidth: '100%', height: '21px', fontSize: '12px', textAlign: justify === 'center' }}
						popupForBigFields
						// the promotion popup sits at the default popup z-index, so
						// the Date/TextArea popups must open above it, like the
						// popup's other alerts
						popupZIndex={999}
						boolButtonNullOption
						onSave={vRaw => {
							const v = vRaw === '' ? null : vRaw;
							setLoadingState(LoadingStateEnum.LOADING);
							// like the assortments Cell the editor stays active after a
							// save: blur closes the text-like editors, a click outside
							// closes the others, and Boolean can cycle its value
							props.onSave(v).then(() => setLoadingState(LoadingStateEnum.LOADED)).catch(() => setLoadingState(LoadingStateEnum.ERROR));
						}}
					/>
				</FlexDiv>
			</TableRow>
		</div>;
	}
	let valueToDisplay = '-';
	if (value !== null && value !== undefined) {
		switch (field.type) {
			case 'Money':
				valueToDisplay = (value as number / 100).toFixed(2);
				break;
			case 'Multiselect':
				valueToDisplay = Array.isArray(value) ? value.length > 0 ? value.join(', ') : '-' : String(value);
				break;
			case 'Date':
				valueToDisplay = moment(value as string).format('L LT');
				break;
			case 'Boolean':
				valueToDisplay = translateToString(value ? 'yes' : 'no');
				break;
			default:
				valueToDisplay = String(value);
		}
	}
	return <TableRow onClick={props.readonly ? undefined : () => setIsActive(true)}>
		<LinkFieldValue justify={justify} title={valueToDisplay}>
			{valueToDisplay}
		</LinkFieldValue>
	</TableRow>;
}


type Props = {
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
	promotion?: Promotion,
	onCreate?: (body: PostPromotionBody) => void,
	onUpdate?: (id: number, body: PutPromotionBody) => void,
	// the promotion-level values after the backend ran the formulas
	onComputed?: (fields: { [key: string]: unknown } | undefined) => void,
	onDelete?: (id: number) => void,
	readOnly?: boolean,
	promotionId?: number
}
type ProductAndCompanyProps = {
	products: PromotionProduct[],
	companies: PromotionCompany[]
}

type ProductAndCompanyHookProps = ProductAndCompanyProps & {
	setProducts: React.Dispatch<React.SetStateAction<PromotionProduct[]>>,
	setCompanies: React.Dispatch<React.SetStateAction<PromotionCompany[]>>
}

const DotWrapper = styled.div`
	position:absolute;
	top: 10px;
	right: 20px;
`;


// the tabs of the right part, products first
const PRODUCTS_TAB = 0;
const COMPANIES_TAB = 1;

function RightPart(props: Props & ProductAndCompanyHookProps & { readOnly?: boolean, promotion?: Promotion }) {
	const { promotion, products, setProducts, companies, setCompanies } = props;
	const [activeIndexRef, setActiveIndex, activeIndex] = useRefState<number>(0);
	const [companyOpen, setCompanyOpen] = React.useState<ModalState<number>>({ isOpen: false });
	const [productOpen, setProductOpen] = React.useState<ModalState<number>>({ isOpen: false });
	const { alertDelete } = React.useContext(AlertContext);
	const { isAllowedTo } = React.useContext(PermissionContext);
	const allowed = isAllowedTo({ objectAction: promotion ? 'UpdateCampaign' : 'CreateCampaign' });

	// bulk association: the + button opens a TSP-like picker (full listing,
	// filters, checkboxes); Add appends to the perimeter, Replace swaps it
	const [perimeterOpen, setPerimeterOpen] = React.useState(false);
	const applyPerimeter = (items: PerimeterItem[], replace: boolean) => {
		const setter = activeIndexRef.current === COMPANIES_TAB ? setCompanies : setProducts;
		setter(list => {
			const base = replace ? [] : list;
			const seen = new Set(base.map(({ id }) => id));
			return [...base, ...items.filter(({ id }) => !seen.has(id))];
		});
	};


	const list: Array<PromotionProduct | PromotionCompany> = activeIndex === PRODUCTS_TAB ? products : companies;
	const onClick = (row: { id: number }) => activeIndexRef.current === COMPANIES_TAB ? setCompanyOpen({ isOpen: true, data: row.id }) : setProductOpen({ isOpen: true, data: row.id });
	const rawFields = usePromotionFieldDefinitions();
	// a field ticked both promotion and product is link-level — one value per
	// (promotion, product) pair — and edited on this grid, one column per field
	const linkFields = React.useMemo(() => rawFields.filter(f => f.is_promotion && f.is_product), [rawFields]);
	// the backend recomputed the formulas on the save: the fresh values of
	// every link, and the promotion level's through the parent
	const refreshComputed = (promotionId: number) => {
		if (!rawFields.some(isComputedField)) return;
		getPromotionById(promotionId).then(fresh => {
			setProducts(products => products.map(product => {
				const link = fresh.products?.find(p => p.id === product.id);
				return link ? { ...product, fields: link.fields } : product;
			}));
			props.onComputed?.(fresh.fields);
		}).catch(() => undefined);
	};

	// sorting is done here (the table only reports the clicked header) and
	// each tab keeps its own sort and column widths, the widths across visits
	const [sorts, setSorts] = React.useState<Record<number, TableSortType | undefined>>({ [PRODUCTS_TAB]: { id: 'name', desc: false }, [COMPANIES_TAB]: { id: 'name', desc: false } });
	const sort = sorts[activeIndex];
	const resizeKey = activeIndex === PRODUCTS_TAB ? 'table_promotion_products_resize' : 'table_promotion_companies_resize';
	const [resizes, setResizes] = React.useState<Record<string, Record<string, number>>>(() => {
		const read = (key: string) => { try { return JSON.parse(localStorage.getItem(key) ?? '{}'); } catch { return {}; } };
		return { table_promotion_products_resize: read('table_promotion_products_resize'), table_promotion_companies_resize: read('table_promotion_companies_resize') };
	});
	const onResize = (value: Record<string, number>) => {
		if (Object.keys(value).length === 0) return;
		setResizes(r => ({ ...r, [resizeKey]: { ...r[resizeKey], ...value } }));
		try { localStorage.setItem(resizeKey, JSON.stringify({ ...resizes[resizeKey], ...value })); } catch { /* private mode */ }
	};
	const sortedList = React.useMemo(() => {
		if (!sort) return list;
		const fieldsById = new Map(linkFields.map(f => [f.id, f]));
		const valueOf = (row: PromotionProduct & PromotionCompany): string | number | undefined => {
			const calc = /^CALC\[(\d+)\]$/.exec(sort.id);
			if (calc) return calculatedSortValue(row.calculated?.[calc[1]]);
			const match = /^LINK_FIELD\[(\d+)\]$/.exec(sort.id);
			if (match) {
				const field = fieldsById.get(Number(match[1]));
				const raw = row.fields?.[match[1]];
				if (!field || raw === null || raw === undefined) return undefined;
				if (typeof raw === 'number') return raw;
				if (typeof raw === 'boolean') return raw ? 1 : 0;
				return promotionFieldDisplay(field, raw).toLowerCase();
			}
			const raw = row[sort.id];
			if (raw === null || raw === undefined) return undefined;
			return typeof raw === 'number' ? raw : String(raw).toLowerCase();
		};
		const direction = sort.desc ? -1 : 1;
		return [...list].sort((a, b) => {
			const va = valueOf(a as PromotionProduct & PromotionCompany);
			const vb = valueOf(b as PromotionProduct & PromotionCompany);
			// blanks last whatever the direction
			if (va === undefined) return vb === undefined ? 0 : 1;
			if (vb === undefined) return -1;
			if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * direction;
			return String(va).localeCompare(String(vb), undefined, { numeric: true }) * direction;
		});
	}, [list, sort, linkFields]);

	const productCalculatedFields = useRecoilValue(APromotionCalculatedFields).filter(cf => cf.target === CalculatedFieldTarget.PromotionProduct);
	// the bounds of each link field's values over the products, for the colours
	const linkRanges = React.useMemo(() => new Map(linkFields.map(f => [f.id, promotionFieldRange(f, products.map(p => p.fields?.[f.id.toString()]))])), [linkFields, products]);

	const columns = React.useMemo((): Column<PromotionProduct & PromotionCompany>[] => {
		const text = (value: string | null | undefined) => <TableRow>{value ? value : '-'}</TableRow>;
		const columns: Column<PromotionProduct & PromotionCompany>[] = [
			{
				id: 'name',
				Header: translateToString('name'),
				accessor: row => <TableRow cursor='pointer' fontWeight='500' onClick={() => onClick(row)}>{row.name}</TableRow>,
				width: 220,
				minWidth: 120,
				type: 'string',
				toolTip: row => row.name,
				hoverImage: row => <TableCellButton onClick={() => onClick(row)} icon={Eye} />
			}
		];
		if (activeIndex === COMPANIES_TAB) {
			columns.push(
				{ id: 'post_code', Header: translateToString('post_code'), accessor: row => text(row.post_code), width: 110, minWidth: 70, type: 'string', disableFilter: true },
				{ id: 'city', Header: translateToString('city'), accessor: row => text(row.city), width: 160, minWidth: 80, type: 'string', disableFilter: true },
			);
		} else {
			columns.push(
				{ id: 'reference', Header: translateToString('reference'), accessor: row => text(row.reference), width: 140, minWidth: 80, type: 'string', disableFilter: true },
				{ id: 'price', Header: translateToString('price'), accessor: row => <TableRow>{row.price === null || row.price === undefined ? '-' : Number(row.price).toFixed(2)}</TableRow>, width: 100, minWidth: 70, type: 'numeric', disableFilter: true },
			);
			// the cells read their value from the row itself, so edits update the
			// rows and never regenerate the columns
			columns.push(...linkFields.map((field): Column<PromotionProduct & PromotionCompany> => ({
				// a relative scale reads over this promotion's products
				id: `LINK_FIELD[${field.id}]`,
				Header: field.name,
				accessor: row => {
					// only products linked server-side have a link row to write to:
					// products added locally become editable once the promotion is saved
					const saved = promotion?.products?.some(p => p.id === row.id) ?? false;
					return <PromotionFieldCell
						field={field}
						value={row.fields?.[field.id.toString()]}
						shown={linkRanges.get(field.id)}
						readonly={props.readOnly || !allowed || !saved}
						onSave={async value => {
							if (!promotion) return;
							await putPromotionProductFields(promotion.id, row.id, { field_id: field.id, value });
							// optimistic update: the row object is shared with
							// promotion.products, so mutate it in place and refresh
							// the array identity for the grid to repaint
							setProducts(products => {
								const product = products.find(p => p.id === row.id);
								if (product) product.fields = { ...product.fields, [field.id.toString()]: value };
								return [...products];
							});
							refreshComputed(promotion.id);
						}}
					/>;
				},
				width: 150,
				minWidth: 80,
				type: field.type === 'Number' || field.type === 'Money' || field.type === 'Integer' ? 'numeric' : 'string',
				disableFilter: true
			})));
			// the promotion x product calculated fields, read-only
			columns.push(...productCalculatedFields.map((cf): Column<PromotionProduct & PromotionCompany> => ({
				id: `CALC[${cf.id}]`,
				Header: cf.field_name,
				accessor: row => <CalculatedValueCell field={cf} value={row.calculated?.[cf.id.toString()]} />,
				width: 150,
				minWidth: 80,
				type: 'numeric',
				disableFilter: true
			})));
		}
		if (!props.readOnly && allowed) {
			columns.push(
				{
					id: 'quick_actions',
					noHeaderEllipsis: true,
					Header: <Add onClick={() => setPerimeterOpen(true)} />,
					accessor: row => <Trash src={trash_black} onClick={() => {
						if (activeIndexRef.current === COMPANIES_TAB) {
							setCompanies(companies => companies.filter(c => c.id !== row.id));
						} else {
							setProducts(products => products.filter(c => c.id !== row.id));
						}
					}} />,
					width: 45,
					minWidth: 45,
					disableSortBy: true,
					disableFilter: true,
					unresizeable: true,
					centerHeader: true
				});
		}
		return columns;
	}, [allowed, activeIndex, linkFields, promotion, props.readOnly, linkRanges, productCalculatedFields]);

	return <>
		{!props.readOnly && <Restricted to={{ objectAction: 'DeleteCampaign' }}>
			<DotWrapper>
				<Dropdown
					dropdownStyle={{
						optionLeft: '-170px',
						optionWidth: '200px',
						optionHeight: '200px',
						height: '25px'
					}}
					datalist={[{
						label: translateToString('delete'),
						value: 'DELETE'
					}]}
					name={'dropdownOptionEvent'}
					JSXButton={() => <Ellipsis strokeWidth={2.5} size={22} color={GreySidely3} />}
					onChange={(value) => {
						switch (value.value) {
							case 'DELETE':
								alertDelete({ name: promotion?.name ?? '', zIndex: 999 }).then(res => {
									if (res.res == AlertRes.Ok && promotion) {
										deletePromotion(promotion.id).then(() => {
											props.setIsOpen(false);
											props.onDelete?.(promotion.id);
										});
									}
								});

						}
					}
					}
					searchable={false}
				/>
			</DotWrapper>
		</Restricted>}
		<PanelSelector panels={[{ imgJsx: <Box />, label: translateToString('general.products') }, { imgJsx: <BuildingComplex />, label: translateToString('companies') }]} onClick={(_, i) => setActiveIndex(i)} activeIndex={activeIndex} />
		<SeperatorWithColor />
		<Table
			// one table per tab: the sort and widths belong to the tab
			key={activeIndex}
			height='calc(100% - 45px)'
			columns={columns}
			data={sortedList}
			onSort={res => setSorts(s => ({ ...s, [activeIndex]: res[0] }))}
			initialSortBy={sort}
			EnableResize
			onResize={onResize}
			resizeValue={resizes[resizeKey]}
		/>
		{/* mounted closed: the popup computes its centered position in an
		effect, so mounting it already open painted one frame at the default
		position and slid to the center */}
		{!props.readOnly && allowed && <>
			<CompanyPerimeterPopup isOpen={perimeterOpen && activeIndex === COMPANIES_TAB} setIsOpen={setPerimeterOpen} onApply={applyPerimeter} />
			<ProductPerimeterPopup isOpen={perimeterOpen && activeIndex === PRODUCTS_TAB} setIsOpen={setPerimeterOpen} onApply={applyPerimeter} />
		</>}
		<ProductPopup
			isOpen={productOpen.isOpen}
			productId={productOpen.data}
			setIsOpen={isOpen => setProductOpen({ isOpen })}
		/>
		<ClientCompany
			isOpen={companyOpen.isOpen}
			setOpen={isOpen => setCompanyOpen({ isOpen })}
			clientCompanyId={companyOpen.data ?? 0}
		/>
	</>;
}

function LeftPart(props: Props & { mode: PopupMode, setMode: React.Dispatch<React.SetStateAction<PopupMode>> } & {
	title: string | undefined,
	setTitle: React.Dispatch<React.SetStateAction<string | undefined>>,
	description: string | undefined,
	setDescription: React.Dispatch<React.SetStateAction<string | undefined>>,
	isActive: boolean,
	setIsActive: React.Dispatch<React.SetStateAction<boolean>>,
	periods: Period[],
	setPeriods: React.Dispatch<React.SetStateAction<Period[]>>,
	promotionFields: { [key: string]: unknown } | undefined,
	setPromotionFields: React.Dispatch<React.SetStateAction<{ [key: string]: unknown }>>
}) {
	const { promotion, title, setTitle, description, setDescription, isActive, setIsActive, periods, setPeriods, promotionFields, setPromotionFields } = props;
	const { alertDelete } = React.useContext(AlertContext);
	const { isAllowedTo } = React.useContext(PermissionContext);
	const rawFields = usePromotionFieldDefinitions();
	// a field ticked both promotion and product is link-level (one value per
	// linked product): it renders on the per-product grid (RightPart) and is
	// excluded from the promotion-level editor
	const fields = React.useMemo(() => rawFields.filter(f => f.is_promotion && !f.is_product), [rawFields]);
	// the promotion calculated fields, computed from the forms filled for it
	const calculatedFields = useRecoilValue(APromotionCalculatedFields).filter(cf => cf.target === CalculatedFieldTarget.Promotion);
	const [areFieldsOpen, setAreFieldsOpen] = React.useState(true);

	return (
		(<PopupCreation
			onModeChange={() => props.setMode(mode => mode === PopupMode.Details ? PopupMode.Default : PopupMode.Details)}
			extended={props.mode === PopupMode.Default}
			onClose={() => props.setIsOpen(false)}
			title={promotion?.name ?? translateToString('new_campagne')}
			messageButton={promotion ? translateToString('save') : undefined}
			canValidate={!props.readOnly && !!title && periods.every(p => p.end && p.start && p.name && moment(p.end).diff(moment(p.start)) > 0)}
			hideValidation
			dropdown={(props.readOnly || props.mode === PopupMode.Default) || !isAllowedTo({ objectAction: 'DeleteCampaign' }) ? undefined : {
				data: [{
					label: translateToString('delete'),
					value: 'DELETE'
				}],
				onChange: value => {
					switch (value.value) {
						case 'DELETE':
							alertDelete({ name: promotion?.name ?? '', zIndex: 999 }).then(res => {
								if (res.res == AlertRes.Ok && promotion) {
									deletePromotion(promotion.id).then(() => {
										props.setIsOpen(false);
										props.onDelete?.(promotion.id);
									});
								}
							});

					}
				}
			}}
		>
			<CreationPopupContainer gap='1em'>
				<Input
					required
					disabled={props.readOnly}
					inputStyle={creationPopupInputStyleMargin}
					name="title"
					type="text"
					placeholder={translateToString('company.creation.event.title')}
					label={translateToString('company.creation.event.title')}
					normalize={[normalizeCapital]}
					onChange={setTitle}
					value={title}
				/>
				<TextAreaContainer>
					<TextAreaLabel>{translateToString('company.creation.event.description')}</TextAreaLabel>
					<TextArea
						disabled={props.readOnly}
						onChange={(e) => {
							setDescription(e.currentTarget.value);
						}} value={description}></TextArea>
				</TextAreaContainer>
				<HorizontalContainer>
					<SwitchText>{translateToString('visible')}</SwitchText>
					<Switch value={isActive} onChange={setIsActive} readonly={props.readOnly} />
				</HorizontalContainer>
				<FlexDiv flow='column' align='stretch' gap='2em'>
					{periods.map((p, i) => <PeriodEditor period={p} index={i + 1} fields={fields} key={`PeriodEditor[${i}]`} setPeriods={setPeriods} readOnly={props.readOnly} periods={periods} />)}
				</FlexDiv>
				{!props.readOnly &&
					<Add onClick={() => setPeriods(p => [...p, genDefaultPeriod()])} />
				}
				{/* under a heading of their own, and collapsible, the way a company's
				    own fields are shown: loose at the foot of the form they read as
				    leftovers rather than a section */}
				{(fields.length > 0 || calculatedFields.length > 0) && <>
					<FlexDiv gap='10px' onClick={() => setAreFieldsOpen(!areFieldsOpen)} clickable>
						<Open isOpen={areFieldsOpen} />
						<TitleSection>{translateToString('additional_columns')}</TitleSection>
					</FlexDiv>
					<Collapse isOpen={areFieldsOpen}>
						<FlexDiv flow='column' align='stretch' width='100%'>
							{fields.map(f => <React.Fragment key={`FIELD[${f.id}]`}>
								<Label>{f.name}{promotionFieldColor(f, promotionFields?.[f.id.toString()]) && <Dot size='10px' color={promotionFieldColor(f, promotionFields?.[f.id.toString()])} style={{ display: 'inline-block', marginLeft: '6px' }} />}</Label>
								{/* the shared editor has no read-only mode: neutralize it so a
								    user without the update right cannot even stage a value */}
								{isComputedField(f)
									? <ComputedValue>{promotionFieldDisplay(f, promotionFields?.[f.id.toString()])}</ComputedValue>
									: <div style={props.readOnly ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
										<StyledFieldEditor value={promotionFields?.[f.id.toString()]} field={f} onSave={(value) => setPromotionFields(pf => ({ ...pf, [f.id.toString()]: value }))} />
									</div>}
							</React.Fragment>)}
							{calculatedFields.map(cf => <React.Fragment key={`CALC[${cf.id}]`}>
								<Label>{cf.field_name}</Label>
								<ComputedValue><CalculatedValueCell field={cf} value={promotion?.calculated?.[cf.id.toString()]} /></ComputedValue>
							</React.Fragment>)}
						</FlexDiv>
					</Collapse>
				</>}
			</CreationPopupContainer>
		</PopupCreation>)
	);

}

// a computed promotion field's value, shown where the others are edited
const ComputedValue = styled.div`
	font-size: 13px;
	padding: 6px 0 10px;
`;

const SaveWrapper = styled.div<{ isOpen: boolean }>`
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	border-top: 1px solid ${BorderColor};
	height: ${({ isOpen }) => isOpen ? 60 : 0}px;
	overflow: hidden;
	translate: 0 ${({ isOpen }) => isOpen ? '0' : 'calc(100% + 10px)'};
	transition: 0.5s;
	padding-top: 10px;
`;

const DEFAULT_POPUP_MODE = PopupMode.Details;

export default function PopupPromotion(props: Props & { isOpen: boolean, mode?: PopupMode }) {
	const [promotion, setPromotion] = React.useState<Promotion | undefined>(props.promotion);
	const rawFields = usePromotionFieldDefinitions();
	const { isAllowedTo } = React.useContext(PermissionContext);
	// the whole popup follows the campaign right, like the API guards: without
	// the update right (create for a new one) every control is read-only
	const readOnly = props.readOnly || !isAllowedTo({ objectAction: promotion ? 'UpdateCampaign' : 'CreateCampaign' });
	const [mode, setMode] = React.useState<PopupMode>(DEFAULT_POPUP_MODE);
	const [products, setProducts] = React.useState<PromotionProduct[]>([...(promotion?.products ?? [])]);
	const [companies, setCompanies] = React.useState<PromotionCompany[]>([...(promotion?.companies ?? [])]);
	const [title, setTitle] = React.useState<string | undefined>(promotion?.name);
	const [description, setDescription] = React.useState<string | undefined>(promotion?.description);
	const [isActive, setIsActive] = React.useState<boolean>(promotion?.is_active ?? true);
	const [periods, setPeriods] = React.useState<Period[]>(promotion?.periods ? JSON.parse(JSON.stringify(promotion.periods)) : [genDefaultPeriod()]);
	const [promotionFields, setPromotionFields] = React.useState<{ [key: string]: unknown } | undefined>(promotion?.fields);
	const alert = useAlert();

	React.useEffect(() => {
		setPromotion(props.promotion);
	}, [props.promotion]);

	React.useEffect(() => {
		if (props.promotionId === undefined) {
			setPromotion(props.promotion);
		} else {
			getPromotionById(props.promotionId).then(setPromotion);
		}
	}, [props.promotionId]);

	React.useEffect(() => {
		if (props.mode !== undefined) setMode(props.mode);
	}, [props.mode]);

	const getDifferencesUpdate = (): PutPromotionBody => {
		if (!promotion) return {
			name: title,
			is_active: isActive,
			periods,
			description: description ? { updated: description } : 'deleted',
			companies_add: companies.map(({ id }) => id),
			products_add: products.map(({ id }) => id),
			fields: promotionFields ? { updated: promotionFields } : 'deleted'
		};
		const body: PutPromotionBody = {};

		if (promotion.is_active !== isActive) body.is_active = isActive;

		if (promotion.name !== title) body.name = title;

		if (promotion.periods.length !== periods.length
			|| promotion.periods.some((p, i) => p.name !== periods[i].name
				|| moment(p.end).toISOString() !== (periods[i].end ? moment(periods[i].end).toISOString() : undefined)
				|| moment(p.start).toISOString() !== (periods[i].start ? moment(periods[i].start).toISOString() : undefined)
				|| (p.color ?? PrimaryColor) !== (periods[i].color ?? PrimaryColor)
				|| JSON.stringify(p.fields) !== JSON.stringify(periods[i].fields)
			)
		) body.periods = periods;

		if (promotion.description === description) body.description = 'unmodified';
		else if (!description) body.description = 'deleted';
		else body.description = { updated: description };

		if (JSON.stringify(promotion.fields) === JSON.stringify(promotionFields)) body.fields = 'unmodified';
		else if (!promotionFields) body.fields = 'deleted';
		else body.fields = { updated: promotionFields };

		const promotionComps = promotion.companies?.map(({ id }) => id).sort((idA, idB) => idB - idA) ?? [];
		const newComps = companies.map(({ id }) => id).sort((idA, idB) => idB - idA);
		if (!equal(promotionComps, newComps)) {
			const deletion = promotionComps.filter(id => !newComps.some(ncid => ncid === id));
			const addition = newComps.filter(id => !promotionComps.some(ncid => ncid === id));
			if (deletion.length > 0) {
				body.companies_del = deletion;
			}
			if (addition.length > 0) {
				body.companies_add = addition;
			}
		}
		const promotionProds = promotion.products?.map(({ id }) => id).sort((idA, idB) => idB - idA) ?? [];
		const newProds = products.map(({ id }) => id).sort((idA, idB) => idB - idA);
		if (!equal(promotionProds, newProds)) {
			const deletion = promotionProds.filter(id => !newProds.some(ncid => ncid === id));
			const addition = newProds.filter(id => !promotionProds.some(ncid => ncid === id));
			if (deletion.length > 0) {
				body.products_del = deletion;
			}
			if (addition.length > 0) {
				body.products_add = addition;
			}
		}
		return body;
	};

	const close = (force: boolean) => {
		if (force || readOnly || Object.values(getDifferencesUpdate()).every(d => typeof d === 'string' && d === 'unmodified')) {
			props.setIsOpen(false);
			setMode(DEFAULT_POPUP_MODE);
			return;
		}
		alert({
			zIndex: 999,
			title: translateToString('unsaved_changes'),
			content: translateToString('unsaved_changes_leave'),
			buttons: [{ title: translateToString('yes'), res: AlertRes.Ok, style: ButtonStyle.White }]
		}).then(res => {
			if (res.res == AlertRes.Ok) {
				props.setIsOpen(false);
				setMode(DEFAULT_POPUP_MODE);
			}
		});
	};

	React.useEffect(() => {
		setProducts([...(promotion?.products ?? [])]);
		setCompanies([...(promotion?.companies ?? [])]);
		setTitle(promotion?.name);
		setDescription(promotion?.description);
		setIsActive(promotion?.is_active ?? true);
		setPeriods(promotion?.periods ? JSON.parse(JSON.stringify(promotion.periods)) : [genDefaultPeriod()]);
		setPromotionFields(promotion?.fields);
	}, [promotion]);

	return <Popup
		isOpen={props.isOpen}
		onClickOut={() => close(false)}
		popupMode={mode}
		popupStyle={{ animate: true, minWidth: '450px' }}
		name='Prom'
	>
		<Restricted to={{ objectAction: 'ReadCampaign' }} fallback={<Unhautorized />}>
			<FlexDiv height='100%' width='100%' flow='column'>
				<FlexDiv height='100%' width='100%' flexShrink={1} minHeight='0'>
					<LeftContainer isFullOpen={mode === PopupMode.Default}>
						<LeftPart {...props}
							readOnly={readOnly}
							promotion={promotion}
							setIsOpen={close}
							mode={mode}
							setMode={setMode}
							title={title}
							setTitle={setTitle}
							description={description}
							setDescription={setDescription}
							isActive={isActive}
							setIsActive={setIsActive}
							periods={periods}
							setPeriods={setPeriods}
							promotionFields={promotionFields}
							setPromotionFields={setPromotionFields}
						/>
					</LeftContainer>
					<RightContainer isFullOpen={mode === PopupMode.Default} isOpen={false}>
						<RightPart {...props}
							// only the computed keys move: the user's unsaved edits stay
							onComputed={fields => setPromotionFields(pf => ({ ...pf, ...Object.fromEntries(Object.entries(fields ?? {}).filter(([key]) => rawFields.some(f => f.id.toString() === key && isComputedField(f)))) }))}
							promotion={promotion}
							products={products}
							setProducts={setProducts}
							companies={companies}
							setCompanies={setCompanies}
							readOnly={readOnly}
						/>
					</RightContainer>
				</FlexDiv>
				<Restricted to={promotion ? { objectAction: 'UpdateCampaign' } : { objectAction: 'CreateCampaign' }}>
					<SaveWrapper isOpen={!readOnly && !Object.values(getDifferencesUpdate()).every(d => typeof d === 'string' && d === 'unmodified')}>
						<DefaultButton
							width='200px'
							disabled={!title || readOnly || Object.values(getDifferencesUpdate()).every(d => typeof d === 'string' && d === 'unmodified') || periods.some(p => !p.end || !p.start || !p.name) || periods.length === 0}
							onClick={() => {
								if (readOnly) return;
								if (promotion?.id) {
									props.onUpdate?.(promotion.id, getDifferencesUpdate());
								} else if (title) {
									props.onCreate?.({
										name: title,
										periods,
										is_active: isActive,
										companies: companies.map(({ id }) => id),
										description,
										products: products.map(({ id }) => id)
									});
								}
								close(true);
							}}><Translate id='save' /></DefaultButton>
					</SaveWrapper>
				</Restricted>
			</FlexDiv>
		</Restricted>
	</Popup>;
}
